import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  setCartData,
  setPromoCodes,
} from '../../../page-builder/redux/slices/cartSlice';
import checkoutService from '../../api/checkoutService';
import {
  marginFields,
  MarginProps,
} from '../../../page-builder/puck/reusable-props/margin';
import {
  convertToSnakeCase,
  getBorderProps,
  getcurrentDeviceType,
  getFontSize,
  getMarginStyles,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { ComponentConfig } from '@measured/puck';
import {
  ReduxSelectorProps,
  ReduxDispatchProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { PromoCodeBadge } from '../promo-code-badge/PromoCodeBadge';
import Loader from '../../../assets/svg/loader';
import {
  ApplyDiscountCodePayload,
  ShippingMethod,
} from '../../interface/checkoutInterface';
import PromoButton from './promoButton';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../puck/reusable-props/inputfield-height';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import { setAvailableShippingMethods } from '../../redux/slices/checkoutSlice';
import { IPaymentAddress } from '../../interface/formInterface';

import { PaymentFormField } from '../payment/PaymentFormOptions';
import { defaultBorderProps } from '../../utils/constants';
import { marginProps } from '../../puck/reusable-props/NewMargin';
import { outlineProps } from '../../puck/reusable-props/Outline';
const ERROR_EMPTY_PROMO = 'Please enter your promo code.';

const ERROR_INVALID_PROMO = 'Invalid promo code.';

const VALID_PROMO = 'Promo code applied successfully';
const VALID_PROMO_REMOVED = 'Promo code removed successfully';

export type PromoProps = {
  textColor: string;
  fontSize: string;
  fontWeight: string;
  text: string;
  backgroundColor: string;
  backgroundColorAI: string;
};

export type PromoCodeProps = {
  placeholder: IPaymentAddress;
  button: PromoProps;
  border: IBorderValueProps;
} & MarginProps &
  showComponentProps &
  fieldHeightProps &
  PaddingProps;

export const PromoCode = ({
  useAppSelector,
  useAppDispatch,
}: ReduxSelectorProps &
  ReduxDispatchProps): ComponentConfig<PromoCodeProps> => {
  return {
    label: (
      <CustomTooltip title="PromoCode">
        <span>Promo Code</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,

      placeholder: {
        type: 'custom',
        label: 'Place Holder',
        render: ({ value, onChange }) => {
          return (
            <PaymentFormField
              key="placeholder"
              name="placeholder"
              value={value}
              onChange={onChange}
            />
          );
        },
      },
      button: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <PromoButton value={value} onChange={onChange} />
        ),
      },
      ...marginFields,
      ...paddingFields,
      ...inputFieldHeightProps(true),
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      placeholder: {
        headers: {
          hide: false,
          text: 'Promo Code',
          ...paddingProps,
          ...marginProps,
        },
        placeholder: 'Promo Code (Case Sensitive)',
        color: '#808080',
        fontSize: '14',
        fontWeight: '300',
        lineHeight: '1.5',
        textAlign: 'left',
        fontFamily: 'sans-serif',
        letterSpace: '0',
        textColor: {
          color: '#000000',
          isInherit: false,
        },
        textBackgroundColor: {
          color: '#ffffff',
          isInherit: false,
        },
        ...outlineProps,
        ...defaultBorderProps,
      },

      button: {
        text: 'Apply',
        backgroundColor: '#e5e7eb',
        backgroundColorAI: '#e5e7eb',
        fontSize: 'text-sm',
        fontWeight: 'font-normal',
        textColor: '#4b5563',
      },
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        rowGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        colGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        deviceType: 'desktop',
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...borderProps,
      ...paddingProps,
    },
    render: ({
      margin,
      showComponent,
      button,
      placeholder,
      heightField,
      editMode,
      border,
      padding,
    }) => {
      const dispatch = useAppDispatch();
      const [searchParams] = useSearchParams();
      const shopId = searchParams.get('store')?.split('.')?.[0];
      const { cartData, promoCodes } = useAppSelector(state => state?.cart);
      const { pageId } = useAppSelector(state => state.checkout);
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const [promoCode, setPromoCode] = useState('');
      const [promoCodeList, setPromoCodeList] = useState<string[]>(
        promoCodes || [],
      );
      const [loading, setLoading] = useState<boolean>(false);
      const [promoMessage, setPromoMessage] = useState({
        text: '',
        status: '',
      });
      const [isInputFocused, setIsInputFocused] = useState(false);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      const handleApplyPromoCode = async () => {
        if (!promoCode.trim()) {
          setPromoMessage({ text: ERROR_EMPTY_PROMO, status: 'error' });
          return;
        }
        if (!(cartData?.ID && shopId)) return;
        const newPromoCodeList = [...promoCodeList, promoCode.trim()];
        const payload = {
          DiscountCodes: newPromoCodeList,
          ID: cartData?.ID,
          ShopID: shopId,
          StoreID: Number(cartData?.StoreID),
          PageID: String(pageId) || '',
        };
        try {
          setLoading(true);
          const res = await checkoutService.checkoutApplyDiscountCode(
            payload,
            cartData,
          );
          dispatch(setCartData(res?.data?.Cart));
          setPromoCodeList(newPromoCodeList);
          dispatch(setPromoCodes(newPromoCodeList));
          setPromoCode('');
          setPromoMessage({ text: VALID_PROMO, status: 'success' });
          setLoading(false);
        } catch (error) {
          setLoading(false);
          setPromoCode('');
          setPromoMessage({ text: ERROR_INVALID_PROMO, status: 'error' });
        }
      };

      const handleRemovePromoCode = async (code: string) => {
        const newPromoCodeList = promoCodeList.filter(promo => promo !== code);
        if (!(cartData?.ID && shopId)) return;
        const payload = {
          DiscountCodes: newPromoCodeList,
          ID: cartData?.ID,
          ShopID: shopId,
          StoreID: Number(cartData?.StoreID),
          PageID: String(pageId) || '',
        };
        setLoading(true);
        try {
          const res = await checkoutService.checkoutApplyDiscountCode(
            payload as ApplyDiscountCodePayload,
            cartData,
          );
          dispatch(setCartData(res?.data?.Cart));
          setCartData(res?.data?.Cart);
          dispatch(
            setAvailableShippingMethods(
              res?.data?.Cart?.AvailableShippingMethods as ShippingMethod[],
            ),
          );
          setPromoCodeList(newPromoCodeList);
          dispatch(setPromoCodes(newPromoCodeList));
          setPromoMessage({ text: VALID_PROMO_REMOVED, status: 'success' });
        } catch (error) {
          setPromoMessage({ text: ERROR_INVALID_PROMO, status: 'error' });
        } finally {
          setLoading(false);
        }
      };

      const messageClass =
        promoMessage.status === 'success' ? 'success-message' : 'error-message';

      useEffect(() => {
        setPromoCodeList(promoCodes || []);
        if (promoMessage.text) {
          const timer = setTimeout(() => {
            setPromoMessage({ text: '', status: '' });
          }, 5000);
          return () => clearTimeout(timer);
        }
      }, [promoMessage, promoCodes]);

      const placeholderclass = convertToSnakeCase(placeholder?.placeholder);

      const forbiddenSymbolsRegex = /[^\x20-\x7E]/g;

      const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();

        const input = e.currentTarget;
        const clipboardData = e.clipboardData.getData('text');
        const sanitizedData = clipboardData.replace(forbiddenSymbolsRegex, '');

        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const currentValue = promoCode;

        const newValue =
          currentValue.slice(0, start) +
          sanitizedData +
          currentValue.slice(end);

        setPromoCode(newValue);

        setTimeout(() => {
          input.setSelectionRange(
            start + sanitizedData.length,
            start + sanitizedData.length,
          );
        }, 0);
      };

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`${border?.borderStyle?.[currentDeviceType]} text-editor-content`}
              style={{
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
                ...getBorderProps(border, currentDeviceType),
              }}>
              {placeholder?.headers?.text && placeholder?.headers?.hide && (
                <div
                  style={{
                    ...getPaddingStyles(
                      placeholder?.headers?.padding,
                      currentDeviceType || 'desktop',
                    ),
                    ...getNewMarginStyles(
                      placeholder?.headers?.margin,
                      currentDeviceType || 'desktop',
                    ),
                  }}
                  className="text-sm mb-1 ml-2"
                  dangerouslySetInnerHTML={{
                    __html: placeholder?.headers?.text,
                  }}
                />
              )}
              <div className="mt-1 flex space-x-4 mb-3">
                <input
                  aria-label="Promo Code"
                  type="text"
                  id="discount-code"
                  name="discount-code"
                  style={{
                    height: heightField
                      ? isNaN(+heightField?.height?.[currentDeviceType])
                        ? heightField?.height?.[currentDeviceType]
                        : +heightField?.height?.[currentDeviceType]
                      : '48px',
                    ...getBorderProps(
                      isInputFocused ? placeholder.outline : placeholder.border,
                      currentDeviceType || 'desktop',
                    ),
                    color: placeholder?.textColor?.isInherit
                      ? 'inherit'
                      : placeholder?.textColor?.color,
                    backgroundColor: placeholder?.textBackgroundColor?.isInherit
                      ? 'inherit'
                      : placeholder?.textBackgroundColor?.color,
                  }}
                  className={`block w-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm placeholder:text-sm custom-placeholder-${placeholderclass} ${isInputFocused ? placeholder?.outline?.borderStyle?.[currentDeviceType] : placeholder?.border?.borderStyle?.[currentDeviceType]}`}
                  value={promoCode}
                  onChange={e =>
                    setPromoCode(
                      e.target.value.replace(forbiddenSymbolsRegex, ''),
                    )
                  }
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setIsInputFocused(false)}
                  onPaste={handlePaste}
                  disabled={loading}
                  placeholder={placeholder?.placeholder}
                />

                <style>
                  {`
     .custom-placeholder-${placeholderclass}::placeholder {
       color: ${placeholder?.color};
       font-size: ${getFontSize(placeholder?.fontSize)};
       font-weight: ${placeholder?.fontWeight};
       height: ${placeholder?.lineHeight};
       text-align: ${placeholder?.textAlign};
       font-family: ${placeholder?.fontFamily};
       letter-spacing: ${placeholder?.letterSpace}px;
     }
   `}
                </style>

                <button
                  id="promo-button"
                  type="button"
                  style={{
                    color: button?.textColor,
                    backgroundColor: promoCode
                      ? button?.backgroundColorAI
                      : button?.backgroundColor,
                  }}
                  className={`rounded-md px-4 ${button?.fontSize} ${button?.fontWeight}  hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50`}
                  onClick={handleApplyPromoCode}
                  disabled={loading}>
                  {loading ? <Loader /> : `${button?.text}`}
                </button>
              </div>
              <div className={messageClass}>{promoMessage?.text}</div>
              <PromoCodeBadge
                promoCodes={promoCodeList}
                handleRemove={handleRemovePromoCode}
                loading={loading}
              />
            </div>
          )}
        </>
      );
    },
  };
};

export default PromoCode;
